"use client";

import { Loader2 } from "lucide-react";
import Link from "next/link";
import {
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useState,
} from "react";
import { tw } from "~/utilities/styles";

type Variant = keyof typeof variantColorStyles;
type Color = keyof (typeof variantColorStyles)["solid"];

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
  variant?: Variant;
  size?: keyof typeof sizeStyles;
  color?: Color;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
  href?: string;
  ref?: Ref<HTMLButtonElement>;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
};

export function Button({ href, ...remaining }: Props) {
  if (href) {
    return (
      <Link href={href}>
        <ButtonComponent {...remaining} />
      </Link>
    );
  }

  return <ButtonComponent {...remaining} />;
}

function ButtonComponent({
  variant = "inherit",
  size = "inherit",
  color = "inherit",
  leftIcon,
  rightIcon,
  className,
  children,
  loading,
  disabled,
  onClick,
  ref,
  ...remaining
}: Omit<Props, "href">) {
  const [asyncLoading, setAsyncLoading] = useState(false);
  const isLoading = loading || asyncLoading;
  const useHoverable =
    variant === "inherit" || variant === "solid" || color === "inherit";

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (!onClick) return;
    const result = onClick(event);
    if (result && typeof result.then === "function") {
      setAsyncLoading(true);
      void result.then(() => {
        setAsyncLoading(false);
      });
    }
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || isLoading}
      className={`${useHoverable ? "not-disabled:hoverable" : ""} inline-flex items-center justify-center gap-1.5 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/15 disabled:cursor-not-allowed disabled:opacity-50 ${variantBaseStyles[variant]} ${sizeStyles[size]} ${variantColorStyles[variant][color]} ${className ?? ""}`}
      onClick={handleClick}
      {...remaining}
    >
      {isLoading ? <Loader2 className="animate-spin" /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}

const variantBaseStyles: Record<Variant, string> = {
  inherit: "",
  solid: tw`font-medium`,
  outline: tw`font-medium`,
  ghost: "",
};

const sizeStyles = {
  inherit: "",
  sm: tw`px-2.5 py-1.5 text-xs`,
  md: tw`px-3.5 py-2 text-[13px]`,
  lg: tw`px-3.5 py-2 text-[13px]`,
};

const variantColorStyles = {
  inherit: {
    inherit: "",
    primary: "",
    secondary: "",
    info: "",
    success: "",
    warn: "",
    error: "",
  },
  solid: {
    inherit: "",
    primary: tw`bg-fg text-surface hover:bg-fg/90`,
    secondary: tw`bg-secondary text-white hover:bg-secondary/90`,
    info: tw`bg-info text-white hover:bg-info/90`,
    success: tw`bg-success text-white hover:bg-success/90`,
    warn: tw`bg-warn text-white hover:bg-warn/90`,
    error: tw`bg-error text-white hover:bg-error/90`,
  },
  outline: {
    inherit: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    primary: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    secondary: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    info: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    success: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    warn: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    error: tw`border border-error/40 bg-surface text-error hover:bg-error/5`,
  },
  ghost: {
    inherit: tw`bg-transparent text-muted hover:bg-sub hover:text-fg`,
    primary: tw`text-muted hover:bg-sub hover:text-fg`,
    secondary: tw`text-muted hover:bg-sub hover:text-fg`,
    info: tw`text-info hover:bg-info/10`,
    success: tw`text-success hover:bg-success/10`,
    warn: tw`text-warn hover:bg-warn/10`,
    error: tw`text-error hover:bg-error/10`,
  },
};
